<template>
  <div class="project-detail-container">
    <!-- 顶部banner -->
    <div class="project-detail-banner">
      <img src="@/assets/topbanner.jpg" alt="工程案例详情" class="banner-img">
    </div>

    <!-- 面包屑导航 -->
    <div class="breadcrumb-container">
      <div class="breadcrumb">
        <router-link to="/">首页</router-link>
        <span class="breadcrumb-separator">></span>
        <router-link to="/product">工程案例</router-link>
        <span class="breadcrumb-separator">></span>
        <router-link :to="`/product?tab=${project.category}`">{{ getCategoryName(project.category) }}</router-link>
        <span class="breadcrumb-separator">></span>
        <span class="breadcrumb-current">{{ project.title }}</span>
      </div>
    </div>

    <!-- 主体内容 -->
    <div class="project-detail-content">
      <!-- 项目信息区域 -->
      <div class="project-info-section">
        <!-- 左侧大图 -->
        <div class="main-image-container">
          <img :src="project.mainImage" :alt="project.title" class="main-image">
        </div>
        
        <!-- 右侧项目信息 -->
        <div class="project-details">
          <h1 class="project-title">{{ project.title }}</h1>
          
          <div class="project-meta">
            <div class="meta-item">
              <span class="meta-label">建设单位：</span>
              <span class="meta-value">{{ project.company }}</span>
            </div>
            <div class="meta-item">
              <span class="meta-label">工程地址：</span>
              <span class="meta-value">{{ project.location }}</span>
            </div>
            <div class="meta-item">
              <span class="meta-label">项目金额：</span>
              <span class="meta-value">{{ project.amount }}</span>
            </div>
            <div class="meta-item">
              <span class="meta-label">工程分类：</span>
              <span class="meta-value">{{ getCategoryName(project.category) }}</span>
            </div>
          </div>
        </div>
      </div>

      <!-- 缩略图预览 -->
      <div class="thumbnail-container">
        <div 
          v-for="(thumb, index) in project.thumbnails" 
          :key="index"
          class="thumbnail-item"
          @click="selectImage(thumb)"
          :class="{ active: project.mainImage === thumb }"
        >
          <img :src="thumb" :alt="`项目缩略图${index + 1}`" class="thumbnail-img">
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted, watchEffect, computed } from 'vue'
import { useRoute } from 'vue-router'
import { useHead } from '@vueuse/head'

const route = useRoute()
const project = ref({
  id: '',
  title: '',
  company: '',
  location: '',
  amount: '',
  category: 4,
  mainImage: '',
  thumbnails: [],
  description: ''
})

// 项目分类映射
const categoryMap = {
  1: '输电工程',
  2: '配电工程',
  3: '新能源工程',
  4: '变电站土建工程'
}

// 获取分类名称
const getCategoryName = (category) => {
  return categoryMap[category] || '其他工程'
}

// 动态SEO Meta标签配置
const meta = useHead({
  title: computed(() => `${project.value.title || '项目详情'} - 华洲电力建设有限公司`),
  meta: [
    {
      name: 'description',
      content: computed(() => {
        if (!project.value.title) return '华洲电力建设有限公司项目详情页'
        return `${project.value.title} - ${project.value.company || ''} - ${project.value.location || ''} - 华洲电力建设有限公司`
      })
    },
    {
      name: 'keywords',
      content: computed(() => {
        const category = getCategoryName(project.value.category)
        return `华洲电力,项目详情,${category},${project.value.company || ''},${project.value.location || ''}`
      })
    },
    {
      name: 'author',
      content: '华洲电力建设有限公司'
    },
    {
      property: 'og:title',
      content: computed(() => `${project.value.title || '项目详情'} - 华洲电力建设有限公司`)
    },
    {
      property: 'og:description',
      content: computed(() => {
        if (!project.value.title) return '华洲电力建设有限公司项目详情页'
        return `${project.value.title} - ${project.value.company || ''} - ${project.value.location || ''} - 华洲电力建设有限公司`
      })
    },
    {
      property: 'og:type',
      content: 'website'
    }
  ]
})

// 选择图片
const selectImage = (imageUrl) => {
  project.value.mainImage = imageUrl
}

// 处理项目数据的辅助函数
const processProjectData = (projectData) => {
  // 安全地访问 cover 数组，避免空数组或 undefined 导致的错误
  const coverImages = Array.isArray(projectData.cover) && projectData.cover.length > 0 
    ? projectData.cover 
    : ['/static/images/anli1.jpg']
  
  // 转换数据结构以适配详情页的格式需求
  project.value = {
    id: projectData._id || projectData.id || route.params.id,
    title: projectData.title || '项目详情',
    company: projectData.company || '未知',
    location: projectData.location || '未知',
    amount: projectData.investment || projectData.amount || '未知金额',
    category: projectData.category || 4,
    mainImage: coverImages[0],
    thumbnails: coverImages,
    description: `<p>项目名称：${projectData.title || '未知'}</p>
                 <p>建设单位：${projectData.company || '未知'}</p>
                 <p>工程地址：${projectData.location || '未知'}</p>
                 <p>工程规模：${projectData.scale || '未知'}</p>
                 <p>投资额：${projectData.investment || projectData.amount || '未知'}</p>
                 <p>这是该项目的详细信息页面。项目类型为${getCategoryName(projectData.category || 4)}。</p>`
  }
}

// 获取项目详情数据
const fetchProjectDetail = () => {
  const projectId = route.params.id || 'demo'
  console.log('开始获取项目详情，ID:', projectId)
  
  // 简化的解决方案：直接使用本地存储的数据作为主要方式，不再依赖路由状态
  // 1. 首先尝试从sessionStorage获取数据
  try {
    console.log('尝试从sessionStorage获取项目数据...')
    const storedDataKey = `project_${projectId}`
    const storedData = sessionStorage.getItem(storedDataKey)
    
    if (storedData) {
      try {
        const projectData = JSON.parse(storedData)
        console.log('✓ 从sessionStorage成功获取到项目数据')
        processProjectData(projectData)
        // 获取成功后清理sessionStorage以避免数据残留
        sessionStorage.removeItem(storedDataKey)
        return
      } catch (parseError) {
        console.error('解析sessionStorage数据时出错:', parseError)
        // 清理损坏的数据
        sessionStorage.removeItem(storedDataKey)
      }
    } else {
      console.log('sessionStorage中未找到ID为', projectId, '的项目数据')
    }
  } catch (storageError) {
    console.error('访问sessionStorage时出错:', storageError)
  }
  
  // 2. 如果sessionStorage不可用，尝试从window全局变量获取（这是一个更可靠的备用方案）
  try {
    if (window.__projectDataCache && window.__projectDataCache[projectId]) {
      console.log('✓ 从全局缓存获取到项目数据')
      const projectData = window.__projectDataCache[projectId]
      processProjectData(projectData)
      // 清理全局缓存
      delete window.__projectDataCache[projectId]
      return
    }
  } catch (globalError) {
    console.error('访问全局缓存时出错:', globalError)
  }
  
  // 3. 增强的模拟数据回退方案
  console.log('使用模拟数据作为回退方案')
  
  // 为常见的ID提供更丰富的模拟数据
  const mockProjects = {
    '1': {
      _id: '1',
      title: '220kV输电线路工程',
      company: '国家电网有限公司',
      location: '北京市海淀区',
      investment: '5000万元',
      category: 1,
      cover: ['/static/images/anli1.jpg', '/static/images/anli2.jpg'],
      scale: '100公里线路'
    },
    '2': {
      _id: '2',
      title: '10kV配电工程改造',
      company: '南方电网公司',
      location: '广东省广州市',
      investment: '2000万元',
      category: 2,
      cover: ['/static/images/anli2.jpg', '/static/images/anli1.jpg'],
      scale: '50公里线路'
    },
    '3': {
      _id: '3',
      title: '10MW光伏发电站项目',
      company: '新能源发展有限公司',
      location: '内蒙古自治区',
      investment: '8000万元',
      category: 3,
      cover: ['/static/images/anli1.jpg', '/static/images/anli2.jpg'],
      scale: '10MW容量'
    },
    '4': {
      _id: '4',
      title: '110kV变电站新建工程',
      company: '电力设计院',
      location: '江苏省南京市',
      investment: '1.2亿元',
      category: 4,
      cover: ['/static/images/anli2.jpg', '/static/images/anli1.jpg'],
      scale: '2×50MVA主变'
    }
  }
  
  // 使用更合理的默认数据
  const defaultProject = {
    _id: projectId,
    title: `项目 #${projectId}`,
    company: '电力建设有限公司',
    location: '中国',
    investment: '待定',
    category: Math.floor(Math.random() * 4) + 1, // 随机选择一个分类
    cover: ['/static/images/anli1.jpg'],
    scale: '标准规模'
  }
  
  // 获取对应ID的模拟数据或默认数据
  const projectData = mockProjects[projectId] || defaultProject
  console.log('创建的模拟项目数据:', projectData)
  processProjectData(projectData)
}

// 组件挂载时获取数据
onMounted(() => {
  fetchProjectDetail()
})
</script>

<style scoped>
.project-detail-container {
  width: 100%;
  min-height: 100vh;
}

/* 顶部banner样式 */
.project-detail-banner {
  width: 100%;
  height: 200px;
  overflow: hidden;
}

.banner-img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

/* 面包屑导航样式 */
.breadcrumb-container {
  width: 1200px;
  margin: 0 auto;
  padding: 20px 0;
  border-bottom: 1px solid #eee;
}

.breadcrumb {
  font-size: 14px;
  color: #666;
}

.breadcrumb a {
  color: #666;
  text-decoration: none;
  transition: color 0.3s ease;
}

.breadcrumb a:hover {
  color: #3f5ff0;
}

.breadcrumb-separator {
  margin: 0 10px;
  color: #999;
}

.breadcrumb-current {
  color: #3f5ff0;
  font-weight: 500;
}

/* 主体内容样式 */
.project-detail-content {
  width: 1200px;
  margin: 0 auto;
  padding: 40px 0;
}

/* 项目信息区域样式 */
.project-info-section {
  display: flex;
  margin-bottom: 30px;
}

.main-image-container {
  flex: 1;
  margin-right: 30px;
}

.main-image {
  width: 100%;
  height: auto;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.project-details {
  flex: 1;
}

.project-title {
  font-size: 24px;
  font-weight: 600;
  color: #333;
  margin-bottom: 30px;
  line-height: 1.4;
}

.project-meta {
  background: #f8f9fa;
  padding: 20px;
  border-radius: 8px;
}

.meta-item {
  display: flex;
  align-items: flex-start;
  margin-bottom: 20px;
  line-height: 1.6;
}

.meta-item:last-child {
  margin-bottom: 0;
}

.meta-label {
  flex: 0 0 100px;
  font-weight: 500;
  color: #666;
}

.meta-value {
  flex: 1;
  color: #333;
}

/* 缩略图预览样式 */
.thumbnail-container {
  display: flex;
  gap: 15px;
  margin-bottom: 40px;
  overflow-x: auto;
  padding-bottom: 10px;
}

.thumbnail-item {
  flex: 0 0 auto;
  width: 120px;
  height: 80px;
  border-radius: 4px;
  overflow: hidden;
  cursor: pointer;
  transition: all 0.3s ease;
  border: 2px solid transparent;
}

.thumbnail-item.active {
  border-color: #3f5ff0;
  box-shadow: 0 0 0 2px rgba(63, 95, 240, 0.2);
}

.thumbnail-img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  transition: transform 0.3s ease;
}

.thumbnail-item:hover .thumbnail-img {
  transform: scale(1.05);
}

/* 项目详情内容样式 */
.project-description {
  background: #f8f9fa;
  padding: 30px;
  border-radius: 8px;
  line-height: 1.8;
  color: #333;
}

.project-description p {
  margin-bottom: 20px;
}

.project-description p:last-child {
  margin-bottom: 0;
}

/* 响应式设计 */
@media (max-width: 1200px) {
  .breadcrumb-container,
  .project-detail-content {
    width: 90%;
  }
}

@media (max-width: 992px) {
  .project-info-section {
    flex-direction: column;
  }
  
  .main-image-container {
    margin-right: 0;
    margin-bottom: 30px;
  }
}

@media (max-width: 768px) {
  .project-detail-banner {
    height: 150px;
  }
  
  .breadcrumb {
    font-size: 12px;
  }
  
  .breadcrumb-separator {
    margin: 0 5px;
  }
  
  .project-title {
    font-size: 20px;
  }
  
  .meta-label {
    flex: 0 0 80px;
    font-size: 14px;
  }
  
  .meta-value {
    font-size: 14px;
  }
  
  .thumbnail-item {
    width: 100px;
    height: 65px;
  }
  
  .project-description {
    padding: 20px;
  }
}

@media (max-width: 480px) {
  .project-detail-content {
    padding: 20px 0;
  }
  
  .meta-item {
    flex-direction: column;
    margin-bottom: 15px;
  }
  
  .meta-label {
    margin-bottom: 5px;
  }
}
</style>